
import React from 'react';
import { Task, TaskType } from '../types';
import { Target, AlertTriangle, Star, Box, Hexagon } from 'lucide-react';

interface RadarViewProps {
  tasks: Task[];
}

const RadarView: React.FC<RadarViewProps> = ({ tasks }) => {
  // Config for the grid
  const gridSize = 600;
  const padding = 50;
  const center = gridSize / 2;
  const axisLength = (gridSize - 2 * padding) / 2;

  // Helper to map vector (-1.0 to 1.0 or 0 to 1.0) to pixels
  // Assuming vectors are roughly -10 to 10 or 0-10 based on standard plotting?
  // Let's normalize assuming 0-5 scale based on your CLI tool (0-5 scores).
  // X: Risk/Complexity (0 to 5)
  // Y: Reward/Impact (0 to 5)
  const scale = (val: number | undefined) => {
    const v = val || 0;
    return (v / 5) * axisLength;
  };

  const getCoordinates = (task: Task) => {
    // Default to center if no vectors
    const x = task.vectorX !== undefined ? task.vectorX : task.riskLevel || 0; 
    const y = task.vectorY !== undefined ? task.vectorY : task.strategicReward || 0;
    
    // Map to SVG coord system (Y is down)
    const px = center + scale(x); 
    const py = center - scale(y);
    return { x: px, y: py };
  };

  return (
    <div className="w-full h-full bg-slate-900 rounded-xl border border-slate-800 shadow-xl flex flex-col">
      <div className="p-4 border-b border-slate-800 flex justify-between items-center">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <Target className="w-5 h-5 text-emerald-400" />
          Strategic Radar
        </h3>
        <div className="flex gap-4 text-xs">
           <div className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500"></span> Critical</div>
           <div className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500"></span> Standard</div>
           <div className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-slate-500"></span> Placeholder</div>
        </div>
      </div>

      <div className="flex-1 overflow-hidden flex items-center justify-center p-4 relative">
         <div className="relative" style={{ width: gridSize, height: gridSize }}>
            
            {/* Grid Background */}
            <svg width={gridSize} height={gridSize} className="absolute inset-0 pointer-events-none">
                {/* Axes */}
                <line x1={padding} y1={center} x2={gridSize - padding} y2={center} stroke="#334155" strokeWidth="2" />
                <line x1={center} y1={padding} x2={center} y2={gridSize - padding} stroke="#334155" strokeWidth="2" />
                
                {/* Concentric Circles (Scale markers 1-5) */}
                {[1, 2, 3, 4, 5].map(r => {
                    const radius = (r / 5) * axisLength;
                    return (
                        <circle key={r} cx={center} cy={center} r={radius} fill="none" stroke="#1e293b" strokeDasharray="4 4" />
                    );
                })}

                {/* Quadrant Labels */}
                <text x={gridSize - padding} y={center + 20} textAnchor="end" className="fill-slate-500 text-xs font-mono">HIGH RISK →</text>
                <text x={padding} y={center + 20} textAnchor="start" className="fill-slate-500 text-xs font-mono">← LOW RISK</text>
                <text x={center + 10} y={padding + 10} textAnchor="start" className="fill-slate-500 text-xs font-mono">↑ HIGH REWARD</text>
                <text x={center + 10} y={gridSize - padding} textAnchor="start" className="fill-slate-500 text-xs font-mono">↓ LOW REWARD</text>
            </svg>

            {/* Plot Points */}
            {tasks.map(task => {
                const { x, y } = getCoordinates(task);
                return (
                    <div 
                        key={task.id}
                        className="absolute group transform -translate-x-1/2 -translate-y-1/2 cursor-pointer transition-all hover:scale-125 hover:z-50"
                        style={{ left: x, top: y }}
                    >
                        {/* Icon based on Type/Criticality */}
                        <div className={`p-1.5 rounded-full shadow-lg border border-slate-700 ${
                            task.isCritical ? 'bg-red-500/20 text-red-400' :
                            task.type === TaskType.PLACEHOLDER ? 'bg-slate-800 text-slate-500' :
                            'bg-blue-600/20 text-blue-400'
                        }`}>
                            {task.type === TaskType.MILESTONE ? <Star className="w-4 h-4 fill-current" /> :
                             task.isCritical ? <AlertTriangle className="w-4 h-4" /> :
                             task.type === TaskType.PLACEHOLDER ? <Box className="w-3 h-3" /> :
                             <Hexagon className="w-3 h-3 fill-current" />}
                        </div>
                        
                        {/* Tooltip */}
                        <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-48 bg-slate-900 border border-slate-700 p-2 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 pointer-events-none z-50">
                            <p className="text-xs font-bold text-white mb-1 truncate">{task.name}</p>
                            <div className="flex justify-between text-[10px] text-slate-400 font-mono">
                                <span>Risk: {task.riskLevel || task.vectorX || 0}</span>
                                <span>Reward: {task.strategicReward || task.vectorY || 0}</span>
                            </div>
                        </div>
                    </div>
                );
            })}
         </div>
      </div>
    </div>
  );
};

export default RadarView;
